import { ReactElement, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@imminently/react/ui/core/header";
import { Text } from "@imminently/react/ui/core/text";
import { ButtonBase, Stack, Typography } from "@mui/material";

import SwitchAccount from "@mui/icons-material/SwitchAccount";
import { AccountMenu } from "@/components/AccountMenu";
import { ActAsModal } from "@/components/ActAsModal";
import PatientSearch from "@/components/PatientSearch";
import { TrierLogoLight } from "@/assets/icons";
import { useCurrentDateTime } from "@/global";
import { IconMenuItem } from "@/components/ContextMenu";
import { css } from "@/theme";
import packageJson from "../../package.json";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useConciergeMode } from "@/contexts/ConciergeModeContext";
import { useActAs } from "@/contexts/ActAsContext";
import { Switch, FormControlLabel, Tooltip } from "@mui/material";

const styles = {
  header: css`
    .MuiToolbar-root {
      padding-left: 24px !important;
    }
  `,
};

const DateTime = () => {
  const now = useCurrentDateTime();

  // Date in format "dd mmm yyyy"
  const date = now.toLocaleDateString(["en-GB"], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  // Time in format "hh:mm"
  const time = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return (
    <Stack justifyContent="center" alignItems="flex-end">
      <Text bold variant="caption">
        {date}
      </Text>
      <Text bold variant="caption">
        {time}
      </Text>
    </Stack>
  );
};

export const AppHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const appVersion = packageJson.version || "-";
  const [actAsModalOpen, setActAsModalOpen] = useState(false);

  const navigateHome = () => {
    navigate("/patients");
  };

  const { isAdmin } = useUserRoles();
  // const canActAs = userRoles.includes("admin") || userRoles.includes("clinician");
  const { isConciergeMode, toggleConciergeMode } = useConciergeMode();
  const { showReauthModal, dismissReauthModal } = useActAs();

  const title = (
    <Stack direction="row" gap={2} alignItems="center">
      {/* Sidebar disabled for now */}
      {/* <IconButton onClick={openSidebar}><Icon icon="menu" /></IconButton> */}

      <ButtonBase
        onClick={navigateHome}
      >
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          alignContent="center"
          justifyContent="flex-start"
          style={{ cursor: "pointer" }}
        >
          <TrierLogoLight
            height={35}
            width={35}
          />
          <Typography
            variant="h6"
          >
            My Emergency Visit
          </Typography>
          <Typography
            variant="caption"
          >
            {appVersion}
          </Typography>
        </Stack>
      </ButtonBase>
      <PatientSearch />
    </Stack>
  ) as string & ReactElement;

  const actions = (
    <Stack direction="row" gap={2} alignItems="center">
      <Tooltip title={isConciergeMode ? t("conciergeMode.toggle.tooltipOn") : t("conciergeMode.toggle.tooltipOff")}>
        <FormControlLabel
          control={
            <Switch
              checked={isConciergeMode}
              onChange={toggleConciergeMode}
              size="small"
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#fff",
                  transform: "translateX(22px)",
                  "& + .MuiSwitch-track": {
                    backgroundColor: (theme) => theme.palette.common.green[500],
                  },
                },
              }}
            />
          }
          label={t("conciergeMode.toggle.label")}
          sx={{ margin: 0 }}
          componentsProps={{
            typography: {
              variant: "caption",
              sx: { fontSize: "0.75rem", whiteSpace: "nowrap" }
            }
          }}
        />
      </Tooltip>
      <DateTime />
      <AccountMenu>
        {isAdmin && (
          <IconMenuItem icon="cog" component={Link} to="/settings">
            Settings
          </IconMenuItem>
        )}
        <IconMenuItem icon={<SwitchAccount />} onClick={() => setActAsModalOpen(true)}>
          Act as...
        </IconMenuItem>
      </AccountMenu>
    </Stack>
  );

  const handleActAsModalClose = () => {
    setActAsModalOpen(false);
    if (showReauthModal) dismissReauthModal();
  };

  return (
    <>
      <Header
        css={styles.header}
        color="primary"
        title={title}
        actions={actions}
      />
      <ActAsModal
        open={actAsModalOpen || showReauthModal}
        onClose={handleActAsModalClose}
        isReauth={showReauthModal}
      />
    </>
  );
};
